import React, { useState } from 'react';
import { Users, TrendingUp, AlertCircle, CheckCircle, Clock, Calendar, ArrowRight, MoreVertical } from 'lucide-react';
const teamMembers = [{
  id: 1,
  name: 'Marco Rossi',
  role: 'Senior Sales Manager',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
  activeDeals: 12,
  capacity: 15,
  utilizationRate: 80,
  status: 'optimal',
  upcomingTasks: 8,
  overdueItems: 0
}, {
  id: 2,
  name: 'Laura Bianchi',
  role: 'Sales Manager',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
  activeDeals: 14,
  capacity: 15,
  utilizationRate: 93,
  status: 'high',
  upcomingTasks: 12,
  overdueItems: 2
}, {
  id: 3,
  name: 'Giuseppe Verdi',
  role: 'Sales Manager',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop',
  activeDeals: 10,
  capacity: 15,
  utilizationRate: 67,
  status: 'optimal',
  upcomingTasks: 6,
  overdueItems: 1
}, {
  id: 4,
  name: 'Sofia Romano',
  role: 'Junior Sales Manager',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
  activeDeals: 7,
  capacity: 12,
  utilizationRate: 58,
  status: 'low',
  upcomingTasks: 4,
  overdueItems: 0
}, {
  id: 5,
  name: 'Andrea Ferrari',
  role: 'Junior Sales Manager',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
  activeDeals: 16,
  capacity: 12,
  utilizationRate: 133,
  status: 'overload',
  upcomingTasks: 15,
  overdueItems: 3
}];
const recentDeals = [{
  id: 1,
  title: 'Negozio Via Montenapoleone',
  brand: 'Gucci',
  assignedTo: 'Marco Rossi',
  priority: 'high',
  dueDate: '2024-01-25',
  status: 'in_progress'
}, {
  id: 2,
  title: 'Boutique Corso Como',
  brand: 'Prada',
  assignedTo: 'Laura Bianchi',
  priority: 'medium',
  dueDate: '2024-01-28',
  status: 'in_progress'
}, {
  id: 3,
  title: 'Store Via della Spiga',
  brand: 'Versace',
  assignedTo: 'Giuseppe Verdi',
  priority: 'low',
  dueDate: '2024-02-02',
  status: 'pending'
}, {
  id: 4,
  title: 'Showroom Brera',
  brand: 'Armani',
  assignedTo: 'Andrea Ferrari',
  priority: 'high',
  dueDate: '2024-01-24',
  status: 'overdue'
}];
export function TeamWorkload() {
  const [selectedMember, setSelectedMember] = useState<number | null>(null);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'optimal':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'high':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'overload':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'optimal':
        return 'Ottimale';
      case 'high':
        return 'Alto';
      case 'low':
        return 'Basso';
      case 'overload':
        return 'Sovraccarico';
      default:
        return 'N/A';
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_progress':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full" data-id="element-3175">
            <Clock className="w-3 h-3" data-id="element-3176" />
            In corso
          </span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-3177">
            <Clock className="w-3 h-3" data-id="element-3178" />
            In attesa
          </span>;
      case 'overdue':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full" data-id="element-3179">
            <AlertCircle className="w-3 h-3" data-id="element-3180" />
            Scaduta
          </span>;
      default:
        return null;
    }
  };
  const totalCapacity = teamMembers.reduce((sum, member) => sum + member.capacity, 0);
  const totalActiveDeals = teamMembers.reduce((sum, member) => sum + member.activeDeals, 0);
  const avgUtilization = teamMembers.reduce((sum, member) => sum + member.utilizationRate, 0) / teamMembers.length;
  return <div className="space-y-6" data-id="element-3181">
      <div data-id="element-3182">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3183">
          Carichi di Lavoro
        </h1>
        <p className="text-sm text-gray-500 mt-1" data-id="element-3184">
          Gestisci e monitora la distribuzione del lavoro nel team
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3185">
        <div className="bg-white border border-gray-200 p-6 rounded-lg" data-id="element-3186">
          <div className="flex items-center justify-between mb-2" data-id="element-3187">
            <span className="text-sm text-gray-500" data-id="element-3188">Trattative Attive</span>
            <TrendingUp className="w-4 h-4 text-[#1388ad]" data-id="element-3189" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-3190">
            <span className="text-2xl font-semibold text-gray-900" data-id="element-3191">
              {totalActiveDeals}
            </span>
            <span className="text-sm text-gray-500" data-id="element-3192">/ {totalCapacity}</span>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-6 rounded-lg" data-id="element-3193">
          <div className="flex items-center justify-between mb-2" data-id="element-3194">
            <span className="text-sm text-gray-500" data-id="element-3195">Utilizzo Medio</span>
            <Users className="w-4 h-4 text-[#1388ad]" data-id="element-3196" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-3197">
            <span className="text-2xl font-semibold text-gray-900" data-id="element-3198">
              {avgUtilization.toFixed(0)}%
            </span>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-6 rounded-lg" data-id="element-3199">
          <div className="flex items-center justify-between mb-2" data-id="element-3200">
            <span className="text-sm text-gray-500" data-id="element-3201">Membri Sovraccarichi</span>
            <AlertCircle className="w-4 h-4 text-red-500" data-id="element-3202" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-3203">
            <span className="text-2xl font-semibold text-gray-900" data-id="element-3204">
              {teamMembers.filter(m => m.status === 'overload' || m.status === 'high').length}
            </span>
            <span className="text-sm text-gray-500" data-id="element-3205">
              / {teamMembers.length}
            </span>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-6 rounded-lg" data-id="element-3206">
          <div className="flex items-center justify-between mb-2" data-id="element-3207">
            <span className="text-sm text-gray-500" data-id="element-3208">Attività in Ritardo</span>
            <Clock className="w-4 h-4 text-red-500" data-id="element-3209" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-3210">
            <span className="text-2xl font-semibold text-gray-900" data-id="element-3211">
              {teamMembers.reduce((sum, m) => sum + m.overdueItems, 0)}
            </span>
          </div>
        </div>
      </div>
      {/* Team Members Workload */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3212">
        <div className="p-6 border-b border-gray-200" data-id="element-3213">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-3214">
            Distribuzione Carichi di Lavoro
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-3215">
          {teamMembers.map(member => <div key={member.id} className="p-6 hover:bg-gray-50 transition-colors" data-id="element-3216">
              <div className="flex items-start gap-4" data-id="element-3217">
                <img src={member.avatar} alt={member.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-3218" />
                <div className="flex-1 min-w-0" data-id="element-3219">
                  <div className="flex items-start justify-between gap-4 mb-3" data-id="element-3220">
                    <div className="flex-1 min-w-0" data-id="element-3221">
                      <h4 className="text-sm font-semibold text-gray-900 truncate" data-id="element-3222">
                        {member.name}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-3223">{member.role}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(member.status)}`} data-id="element-3224">
                      {getStatusLabel(member.status)}
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-3225">
                    {/* Utilization Bar */}
                    <div data-id="element-3226">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1" data-id="element-3227">
                        <span data-id="element-3228">Utilizzo</span>
                        <span className="font-semibold" data-id="element-3229">
                          {member.activeDeals} / {member.capacity} trattative (
                          {member.utilizationRate}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-3230">
                        <div className={`h-2 rounded-full transition-all ${member.utilizationRate > 100 ? 'bg-red-500' : member.utilizationRate > 85 ? 'bg-yellow-500' : member.utilizationRate > 60 ? 'bg-green-500' : 'bg-blue-500'}`} style={{
                      width: `${Math.min(member.utilizationRate, 100)}%`
                    }} data-id="element-3231" />
                      </div>
                    </div>
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-3232">
                      <div className="flex items-center gap-1" data-id="element-3233">
                        <Calendar className="w-3 h-3" data-id="element-3234" />
                        <span data-id="element-3235">
                          {member.upcomingTasks} attività in programma
                        </span>
                      </div>
                      {member.overdueItems > 0 && <div className="flex items-center gap-1 text-red-600" data-id="element-3236">
                          <AlertCircle className="w-3 h-3" data-id="element-3237" />
                          <span data-id="element-3238">{member.overdueItems} in ritardo</span>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
      {/* Recent Deals Distribution */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3239">
        <div className="p-6 border-b border-gray-200" data-id="element-3240">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-3241">
            Trattative Recenti
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-3242">
          <table className="w-full" data-id="element-3243">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-3244">
              <tr data-id="element-3245">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3246">
                  Trattativa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3247">
                  Brand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3248">
                  Assegnato a
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3249">
                  Priorità
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3250">
                  Scadenza
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3251">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3252">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-3253">
              {recentDeals.map(deal => <tr key={deal.id} className="hover:bg-gray-50" data-id="element-3254">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3255">
                    <div className="text-sm font-medium text-gray-900" data-id="element-3256">
                      {deal.title}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3257">
                    <div className="text-sm text-gray-500" data-id="element-3258">{deal.brand}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3259">
                    <div className="text-sm text-gray-900" data-id="element-3260">
                      {deal.assignedTo}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3261">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(deal.priority)}`} data-id="element-3262">
                      {deal.priority === 'high' ? 'Alta' : deal.priority === 'medium' ? 'Media' : 'Bassa'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3263">
                    <div className="text-sm text-gray-500" data-id="element-3264">{deal.dueDate}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3265">
                    {getStatusBadge(deal.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm" data-id="element-3266">
                    <button className="text-gray-400 hover:text-gray-600" data-id="element-3267">
                      <MoreVertical className="w-4 h-4" data-id="element-3268" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}